/**
 * Runtime v3 — typed refusals.
 *
 * A refusal is a RESULT, not an exception message. Every one names a code from
 * a closed vocabulary, carries structured details, and may carry a
 * `counterProposal`: the nearest request the runtime WOULD accept. A refusal
 * without a counter-proposal is honest; a refusal that invents an answer is not.
 *
 * The shape deliberately mirrors `metric-query-compiler.ts`'s
 * `MetricQueryCompilerRefusal` so a reader who knows one knows the other. It is
 * re-declared rather than imported because that module reaches the compiled
 * metric registry, which this runtime may never import (ticket #7765, spec §6).
 */

import type { RuntimeV3JsonObject } from './common';

export const RUNTIME_V3_REFUSAL_CODES = {
  /** No tool, dataset or definition in the world can serve the request at all. */
  noSupportedPath: 'no_supported_path',
  /**
   * AN OBSERVATION WITH ZERO VALUES IS NOT AN ANSWER.
   *
   * The plan was admitted, the revision was read, and the window held nothing
   * the request asked for. Reporting that to the loop as `answered` made the
   * completion decision count an empty envelope as an answer, so a turn could
   * publish `partial_answer` over nothing at all.
   *
   * The WIRE VALUE is deliberately the repo's existing closed-set member
   * `empty_result` (`ask-helix/governed-blocked-reasons.ts`), not a new
   * string. That code already means "was consulted and found nothing", already
   * buckets to the `missing_data` turn outcome
   * (`manager-blocked-reason-outcome.ts`), and already carries reader copy
   * (`turn-outcome.ts`). Minting a second word for a fact this vocabulary
   * already names would give the loop two ways to say one thing, and only one
   * of them would be wired to the copy a reader sees.
   */
  emptyObservation: 'empty_result',
  /** A measure ref that no active revision declares. */
  unknownMeasure: 'unknown_measure',
  /** A dimension ref that the pinned revision's catalog does not declare. */
  unknownDimension: 'unknown_dimension',
  /** A member ref that the pinned dimension does not contain. */
  unknownDimensionMember: 'unknown_dimension_member',
  /** The referenced dataset revision does not exist, or is not readable in this scope. */
  unknownRevision: 'unknown_revision',
  /** A-13 — a revision whose publication contract is not complete cannot be activated. */
  revisionPublicationIncomplete: 'revision_publication_incomplete',
  /** A-13 — the requested aggregation is not one the measure declares as legal. */
  aggregationNotLegal: 'aggregation_not_legal',
  /** A-3 — the requested sum crosses a dimension the measure is not additive over. */
  aggregationNotAdditive: 'aggregation_not_additive',
  /**
   * #7955 — THE MEASURE'S DECLARED AGGREGATION HAS NO EXECUTABLE FORM.
   *
   * Legal for the measure and still not computable in the store. That is a
   * fact about the runtime, not about the request, so it is a typed refusal
   * naming the measure and its aggregation — never an uncaught throw that
   * leaves the turn a `failed` run with no receipt a reader can open.
   */
  unsupportedAggregation: 'unsupported_aggregation',
  /**
   * #7954 — A ROW THE RULE ADMITTED CARRIES NO SET IDENTITY.
   *
   * The conditional rule says a sum across the reopened dimension is lawful
   * INSIDE one declared set. A row that passes the coherence test but names no
   * set cannot be placed inside one, so whether the sum crossed a boundary is
   * not knowable — and summing it anyway would make "this figure includes rows
   * nobody can identify" indistinguishable from "the window held nothing".
   * The dimension is nullable by declaration, so this is structural rather
   * than a property of today's rows, and it fails CLOSED.
   */
  missingCohortIdentity: 'missing_cohort_identity',
  /**
   * #7955 — ONE FIGURE OVER TWO POPULATIONS.
   *
   * A measure may declare the dimensions that BOUND the population it is
   * measured over — for a weighted average, the partitions whose denominators
   * mean different things. An output group spanning two of them produces one
   * number computed over two subjects, which is arithmetically clean and
   * answers no question. The aggregation is supported; the population is
   * mixed, and the code says which.
   */
  mixedPopulation: 'mixed_population',
  /**
   * #7943 — UNLIKE UNITS WOULD HAVE BEEN ADDED INTO ONE FIGURE.
   *
   * Reserved for exactly that. Asking for several measures that carry
   * different units is NOT this: every figure keeps its own unit and the
   * answer comes back as one figure set per unit. The old guard refused that
   * case and published it as `unknown_measure` — a code that says the measure
   * does not exist — so a run-rate beside a one-time cost was reported to the
   * reader as a missing definition. `unknown_measure` is reserved for a ref
   * that does not resolve.
   */
  mixedUnits: 'mixed_units',
  /** A-13 — the requested grain is finer than the measure is measured at. */
  grainBelowNative: 'grain_below_native',
  /** A definition ref that the registry does not hold. */
  unknownDefinition: 'unknown_definition',
  /** A definition reference that names a range rather than one exact version. */
  unresolvedDefinitionVersion: 'unresolved_definition_version',
  /** Approved content was changed; the stored digest no longer matches the payload. */
  definitionDigestMismatch: 'definition_digest_mismatch',
  /**
   * A definition artifact's payload is missing something its kind requires.
   *
   * Minted rather than reused, unlike `empty_result`: `unsupported_argument`
   * is about a TOOL CALL's arguments, and nothing in this set says "the stored
   * definition is not well formed". Today the one use is a metric that
   * declares no `bindingFormat`/`claimDomain`, which the publication path
   * needs in order to print the figure at all.
   */
  invalidDefinitionContent: 'invalid_definition_content',
  /** A lifecycle transition that the state machine does not allow from the current status. */
  invalidLifecycleTransition: 'invalid_lifecycle_transition',
  /** A retired definition cannot be selected, activated or depended upon. */
  definitionRetired: 'definition_retired',
  /** The requested period form cannot be resolved against the revision's grain or coverage. */
  invalidPeriodWindow: 'invalid_period_window',
  /**
   * #8403 — A DECLARED PERIOD BASIS CANNOT APPLY TO THE REQUESTED FORM.
   *
   * A point-in-time measure is read as of one date; a request spanning more
   * than one bucket at its own grain is a different question (a window, not
   * a snapshot), and answering it by silently picking one bucket's value
   * would be exactly the substitution the basis exists to prevent. Carries a
   * counter-proposal naming the legal form.
   */
  basisWindowMismatch: 'basis_window_mismatch',
  /**
   * #7965 — THE PLAN RESOLVED SEVERAL WINDOWS AND THE RUNTIME CANNOT PUBLISH
   * A FIGURE PER WINDOW.
   *
   * Fail-closed, and deliberately not `invalid_period_window`: the window is
   * valid and the revision covers it. What the runtime cannot do is emit one
   * cell per bucket, and collapsing the list to a single span published the
   * whole-revision total under a series label — a governed number at the wrong
   * scope, which the anti-fabrication rule treats as fabrication. A period
   * form whose series the compiler cannot build refuses here rather than
   * answering a different question with a real number.
   */
  periodSeriesUnsupported: 'period_series_unsupported',
  /**
   * #7965 — FEWER CELLS CAME BACK THAN THE PLAN RESOLVED BUCKETS.
   *
   * A series answer with a bucket missing is not a shorter series; it is a
   * series whose reader cannot tell which bucket is absent. The refusal names
   * the buckets that produced no cell, so the gap is stated rather than
   * silently closed up.
   */
  periodSeriesIncomplete: 'period_series_incomplete',
  /**
   * #7966 — A FILTER NAMED A MEMBER THE INDEX CANNOT EXPAND.
   *
   * The member exists in the dimension and other members declare it as their
   * parent, but the dimension declares no `levels`, so no hierarchy index is
   * built and the member expands to itself. `in` then matches the zero rows
   * the parent posts, and `not_in` excludes nothing at all — the second is a
   * silently un-excluded total. Neither is an answer, so both refuse.
   */
  memberNotExpandable: 'member_not_expandable',
  /** The revision declares no grain compatible with the request. */
  grainNotSupported: 'grain_not_supported',
  /** The plan would exceed the declared budget; the counter-proposal narrows it. */
  budgetExceeded: 'budget_exceeded',
  /**
   * The actor does not hold every scope reference the accepted plan requires,
   * as derived from the plan's own resolved references. It says nothing about
   * row-level or column-level permission, which this runtime does not model.
   */
  notAuthorized: 'not_authorized',
  /**
   * A reference INSIDE AN ACCEPTED PLAN crosses a tenant boundary. Never
   * coerced to the caller's tenant.
   *
   * Only used where the caller already owns the thing being described — it is
   * being told about content it supplied itself — so the details never carry a
   * tenant id the caller did not already send. Looking a run up BY ID is not
   * such a case; that answers `runNotFound`.
   */
  crossTenantReference: 'cross_tenant_reference',
  /** An argument that is neither a literal span of the request
   * nor a reference discovery returned. */
  unsupportedArgument: 'unsupported_argument',
  /**
   * NO RUN UNDER THIS ID, FOR THIS CALLER.
   *
   * Deliberately one code for two situations — the run does not exist, and the
   * run belongs to another tenant. A distinct "wrong tenant" answer would turn
   * a run id into an existence oracle: a caller could walk ids and learn which
   * ones are real somewhere else. The two answers are the same code, the same
   * message, the same details and the same query, so there is nothing to
   * compare.
   */
  runNotFound: 'run_not_found',
  /**
   * The run is the caller's, but it is not in a state that may be executed: it
   * has already finished, or another caller is executing it right now.
   * Execution is one-way, so this is never retried into success.
   */
  runNotExecutable: 'run_not_executable',
  /** The run is the caller's, but its saved plan cannot be replayed faithfully. */
  runNotReplayable: 'run_not_replayable',
  /**
   * Two callers raced for an invariant the store serializes — activating two
   * versions of one definition at once. One wins; the other is told plainly
   * rather than silently overwriting it.
   */
  concurrentActivation: 'concurrent_activation',
  /**
   * #7764 I10a — A `derive` INPUT NAMES A RUN THAT CARRIES NO SUCH FIGURE.
   *
   * The run is the caller's — a foreign or absent one answers `run_not_found`,
   * for the existence-oracle reason above — but it holds nothing the arithmetic
   * could read: it refused, it has not finished, or it published no value under
   * that ref. Minted rather than reused because nothing in this set said it.
   * `empty_result` means a window was consulted and held no rows, which is a
   * statement about DATA; this is a statement about a RUN, and telling a reader
   * "the data held nothing" when the truth is "the run you named was refused"
   * is the class of wrong this runtime exists to prevent.
   *
   * Division by zero is deliberately NOT here: `assertResultValuePublishable`
   * already refuses a zero-denominator quotient as `unsupported_argument`
   * ('a ratio with a zero denominator states no quotient'), and a second code
   * for one fact would give the loop two ways to say the same thing.
   */
  deriveInputUnavailable: 'derive_input_unavailable',
  /**
   * RESERVED FOR #7780 — the `scenario` plan kind. UNUSED TODAY.
   *
   * The plan kind itself is already declared in `runs.ts`; these five are the
   * refusals a scenario tool will need. They are reserved here so the
   * vocabulary — and the reader sentence every code must carry in
   * `governed-refusal-narration.ts`, which is total over this union by type —
   * lands in one reviewed edit rather than arriving piecemeal with the tool.
   * Nothing emits them until that ticket registers the tool.
   */
  scenarioCannotMutateLedger: 'scenario_cannot_mutate_ledger',
  scenarioResultNotRealized: 'scenario_result_not_realized',
  scenarioBaselineNotPinned: 'scenario_baseline_not_pinned',
  scenarioParamsInvalid: 'scenario_params_invalid',
  scenarioGraphNotExecutable: 'scenario_graph_not_executable',
  /**
   * #7868 (AT-09) — AN INCOMPLETE PERIMETER IS NEVER LABELLED A TOTAL.
   *
   * A span with buckets that carry no rows still has a sum, and that sum is
   * not the total the span names. Reporting the gap beside the figure is not
   * enough: the figure is already out, under a label claiming a period it does
   * not cover. The figure is withheld instead, and the gap is what is told.
   */
  coverageIncompleteTotalWithheld: 'coverage_incomplete_total_withheld',
  /**
   * #7868 (AT-16) — A MODELED SCOPE MAY NOT BE PRESENTED AS ACTUALS.
   *
   * A modeled scope can be summed, compared and broken down; what it cannot do
   * is answer as private-company actuals, as complete books, as cash flow or as
   * causal ROI. That is not a disclaimer to add afterwards — it decides whether
   * the question was answerable at all, so it is refused where the claim would
   * be made rather than footnoted under it.
   */
  scopeNotActuals: 'scope_not_actuals',
  /**
   * #7815 I5 — THE BRIDGE'S OWN VOCABULARY.
   *
   * NINE codes: exactly the ones the composing operator publishes, so it stops
   * publishing a nearest-fit code with its real name buried in the refusal
   * details.
   *
   * NINE AND NOT ELEVEN. Two of the operator's thirteen reasons publish an
   * EXACT fit that this vocabulary already carries — `mixed_units` ("unlike
   * units would have been added into one figure") and `missing_cohort_identity`
   * ("a row the rule admitted carries no set identity") say precisely what the
   * bridge means by them. Names were briefly reserved beside those two and are
   * now retired: a code the operator never publishes is dead weight in a closed
   * union and a sentence nobody can reach in the narration record. Minting a
   * second word for a fact this vocabulary already names would also give the
   * loop two ways to say one thing, with only one wired to reader copy.
   *
   * The one that carries the whole engine is `bridgeUndeclaredOverlap`: a
   * combined claim whose contributions draw on one population is refused even
   * though the values reconcile perfectly against the target, because
   * `sum(contributions) == total` cannot detect the same dollars twice.
   */
  bridgePlanNotExecutable: 'bridge_plan_not_executable',
  bridgeContributionNotRunBacked: 'bridge_contribution_not_run_backed',
  bridgeContributionValueNotBound: 'bridge_contribution_value_not_bound',
  bridgePopulationDimensionsIncomplete: 'bridge_population_dimensions_incomplete',
  bridgeUndeclaredOverlap: 'bridge_undeclared_overlap',
  bridgeSharesDoNotClose: 'bridge_shares_do_not_close',
  bridgeAllocationAmbiguous: 'bridge_allocation_ambiguous',
  bridgePhasingDoesNotReconcile: 'bridge_phasing_does_not_reconcile',
  bridgeResultNotRealized: 'bridge_result_not_realized',
  /**
   * #8003 — TWO DATASETS, ONE BARE DIMENSION REF, TWO DIFFERENT ANSWERS TO
   * "HOW MAY IT BE NAMED".
   *
   * `discover` resolves the alias policy for a dataset's dimensions from
   * every ACTIVE binding definition a tenant holds. That resolution is keyed
   * per (datasetRef, dimensionRef) and cannot itself be confused by a same-
   * named dimension in an unrelated dataset — but a bare ref shared across
   * two datasets with two different policies is still a fact worth stopping
   * on: any future reader that is not dataset-scoped (an operator naming a
   * dimension in conversation, a cross-dataset tool, a cache keyed by bare
   * ref) inherits the same ambiguity `org_positions.level` once did, silently,
   * until the collision is found by the wrong query answering. So a binding
   * that would CREATE such a collision is refused at activation, before the
   * ambiguous state can exist at all — never discovered later by a flipped
   * answer.
   */
  aliasPolicyCollision: 'alias_policy_collision',
  /**
   * #8433, #8307 — a second dataset declaring a family another dataset
   * already serves on the same basis, so a reader naming that family and
   * basis would have to ask which dataset was meant.
   */
  providerFamilyBasisCollision: 'provider_family_basis_collision',
  /**
   * #8148 I14-05 — THE INTERPRETATION CALL RETURNED NO SCHEMA-VALID DRAFT.
   *
   * Reserved for I14-04's interpretation service; declared here so the
   * central slot-policy mapping (`slot-policy.ts`) is total over every reason
   * it names, rather than growing this vocabulary piecemeal per consumer.
   */
  couldNotInterpret: 'could_not_interpret',
  /** #8148 I14-05 — the question matches no governed vocabulary term at all. */
  outOfScope: 'out_of_scope',
  /**
   * #8148 I14-05 — REUSED WIRE VALUES, DELIBERATELY (see `emptyObservation`
   * above for the precedent). `RuntimeV3ResolutionGapKind`
   * (`analytical-resolution.ts`) already names `source_unavailable`,
   * `member_unresolved` and `unsupported_calculation` for exactly these
   * facts at the per-output resolution layer; the slot-policy mapping table
   * needs the same facts addressable as turn-level refusal codes, and
   * minting a second word for one already-named fact would give the loop two
   * ways to say it with only one wired to reader copy.
   */
  sourceUnavailable: 'source_unavailable',
  /**
   * THE ENTITY EXISTS, ITS RELATIONSHIP DATA DOES NOT (Phoenix IT synergy,
   * 2026-09-24). A relationship graph publishes every deal it knows and marks
   * the ones whose commitments carry no relationship rows as `missing`
   * (docs/runtime-v3/data-contracts/relationships.md). A membership question
   * about such an entity is refused with this code — never answered as zero
   * members, and never answered from another deal's relationships.
   */
  missingRelationshipData: 'missing_relationship_data',
  memberUnresolved: 'member_unresolved',
  unsupportedCalculation: 'unsupported_calculation',
  /**
   * Incident 2026-09-18 — activating `semantic_families` v2 retired v1 while
   * `org_positions.provider_relationships` v1 (active) pinned v1's exact
   * digest, so an active dependent was stranded silently and the whole
   * tenant refused at READ time. Activation now refuses at the WRITE that
   * would strand it: a version may not be superseded while another active
   * definition still depends on its exact digest.
   */
  activationWouldStrandDependents: 'activation_would_strand_dependents',
  /**
   * #9330 B3 — the question needs the reader to pick what it refers to, and no governed query
   * could list the options (a clarification without choices, after its one repair). Refused
   * with a reason rather than published as a question with nothing to pick from.
   */
  clarificationUnresolved: 'clarification_unresolved',
  /** The model turn ended without a final answer that passed envelope verification. */
  finalAnswerUnavailable: 'final_answer_unavailable',
} as const;

export type RuntimeV3RefusalCode =
  (typeof RUNTIME_V3_REFUSAL_CODES)[keyof typeof RUNTIME_V3_REFUSAL_CODES];

export const RUNTIME_V3_REFUSAL_CODE_VALUES: readonly RuntimeV3RefusalCode[] = Object.freeze(
  Object.values(RUNTIME_V3_REFUSAL_CODES),
);

/**
 * The nearest request the runtime would accept.
 *
 * Typed as a partial of the refused input so it can only ever be a NARROWING
 * of what was asked — never a different question. Producers leave it absent
 * when no narrowing exists; absence is a legitimate, honest outcome.
 */
export type RuntimeV3CounterProposal<TInput = RuntimeV3JsonObject> = Readonly<{
  /** Why this narrower form is offered, in the producer's own words. */
  rationale: string;
  /** The narrowed input. Never carries a value, only a request shape. */
  input: Partial<TInput>;
}>;

export type RuntimeV3Refusal<TInput = RuntimeV3JsonObject> = Readonly<{
  code: RuntimeV3RefusalCode;
  /** Reader-facing statement of what was refused and why. Never a stack trace. */
  message: string;
  /** Structured particulars: the refs that failed, the budget that was exceeded. */
  details: RuntimeV3JsonObject;
  counterProposal?: RuntimeV3CounterProposal<TInput>;
  /**
   * #8352 — WHETHER THIS REFUSAL TAKES THE WHOLE TURN OR ONE OUTPUT.
   *
   * Optional in PR A: this is the type only, classification at the three
   * post-preparation sites (`analytical-request.service.ts`, `query.tool.ts`,
   * `interpreted-arm.service.ts`) is a later PR's behaviour change. `turn`:
   * tenant/authorization/integrity/budget refuse the whole answer, exactly as
   * today. `output`: a preparation-level failure (missing definition,
   * unrepresentable, capability gap, clarification) populates the
   * requested-output ledger and lets siblings publish.
   */
  scope?: 'turn' | 'output';
}>;

/** A result that either succeeded with a value or refused with a typed code. */
export type RuntimeV3Outcome<TValue, TInput = RuntimeV3JsonObject> =
  | Readonly<{ ok: true; value: TValue }>
  | Readonly<{ ok: false; refusal: RuntimeV3Refusal<TInput> }>;

export function runtimeV3Refused<TValue, TInput = RuntimeV3JsonObject>(
  code: RuntimeV3RefusalCode,
  message: string,
  details: RuntimeV3JsonObject,
  counterProposal?: RuntimeV3CounterProposal<TInput>,
): RuntimeV3Outcome<TValue, TInput> {
  return {
    ok: false,
    refusal: counterProposal
      ? { code, message, details, counterProposal }
      : { code, message, details },
  };
}

export function runtimeV3Accepted<TValue, TInput = RuntimeV3JsonObject>(
  value: TValue,
): RuntimeV3Outcome<TValue, TInput> {
  return { ok: true, value };
}

/**
 * #8352 X2 — STAMP `scope` ONTO AN ALREADY-BUILT OUTCOME, NEVER RECONSTRUCT
 * IT.
 *
 * The one place a refusal's `scope` is ever assigned. A successful outcome
 * passes through unchanged; a refused one is returned with every other field
 * untouched and `scope` set from the caller's classification. This package
 * cannot know WHICH scope a given call site is — that classification is a
 * maintained inventory the runtime owns (`apps/api/.../refusal-sites.ts`,
 * X2) — so this is deliberately the total, generic half of the wrapper, and
 * the inventory-keyed half (`runtimeV3RefusedAt`) is built on top of it
 * there, never duplicated here.
 */
export function runtimeV3RefusalScoped<TValue, TInput = RuntimeV3JsonObject>(
  outcome: RuntimeV3Outcome<TValue, TInput>,
  scope: 'turn' | 'output',
): RuntimeV3Outcome<TValue, TInput> {
  return outcome.ok ? outcome : { ok: false, refusal: { ...outcome.refusal, scope } };
}

/**
 * #8352 X2 review cure r3 (HOLD @ 65b7cc11, finding 1) — THE ONE LOSSLESS
 * PROJECTION BACK TO THE LEGACY, UNSCOPED WIRE SHAPE.
 *
 * `scope` is new, internal classification (X2) — a fact the runtime now
 * KNOWS about a refusal, not a fact any served envelope or persisted
 * receipt has ever carried. Two egress points serve a `RuntimeV3Refusal`
 * whole today (the query tool's result envelope and its refusal-receipt
 * persistence, `query.tool.ts`) and neither transport change is this PR's
 * to make — so `scope` reaches them ONLY by being stripped here, in the one
 * shared place both call, rather than by two calls quietly staying
 * unscoped by omission (which the next refusal site to route through
 * `runtimeV3RefusedAt` would silently break). Every other field survives
 * untouched — this is a projection, not a rebuild, so a future field this
 * package adds to `RuntimeV3Refusal` still reaches the wire without a
 * second edit here.
 */
export function legacyRefusalProjection<TInput = RuntimeV3JsonObject>(
  refusal: RuntimeV3Refusal<TInput>,
): RuntimeV3Refusal<TInput> {
  const { scope, ...legacy } = refusal;
  return legacy;
}
